/**
 * THE INDIRECT TERM — ONE RESPONSIBILITY, AND THE SCENE MAY ONLY HAVE ONE IMPLEMENTATION OF IT.
 *
 * ── Why this file exists ────────────────────────────────────────────────────────────────────────
 *
 * `HemisphereLight` and `SparseProbeVolume` are not two features. They are two implementations of
 * the SAME thing: *the light that arrives at a surface from everywhere that is not a shadow-casting
 * light*. One is a constant approximated by two colours and a normal; the other is that same
 * integral measured at points in space and interpolated. Running both adds the indirect term to
 * the frame **twice**.
 *
 * That is not a theory. It is what we shipped, and it invalidated every GI measurement taken
 * before 2026-08-31:
 *
 *   - `scene.ts` added a `HemisphereLight` at **1.25** — an unshadowed, position-INDEPENDENT fill
 *     that reaches into the arcade and under every arch.
 *   - `?gi=probe` then added a probe volume whose entire selling point is that it is
 *     position-DEPENDENT: bright in the atrium, dark under the arcade.
 *   - A spatial variation added on top of a constant that already covers the same ground cannot be
 *     seen. The G2.5 measurement duly found "a position-varying warm ambient fill, not bounce
 *     light" — and its own control (the open atrium, +12.9 %) moved nearly as much as its test (the
 *     covered arcade, +18.7 %), which is the signature of a SECOND ambient being added, not of
 *     indirect light finding an interior.
 *   - The `?hemi=` knob to test exactly this was built in R1.6-F and then **never turned**. Every
 *     A/B pair, matrix cell and screenshot since was taken at the 1.25 default.
 *
 * ── The reference implementation agrees ─────────────────────────────────────────────────────────
 *
 * (C) three.js `examples/webgpu_lightprobes_sponza.html` (dev branch, retrieved 2026-08-31) is the
 * official demo of the module we originally vendored into `render/gi/probeGrid.ts` — its
 * `LightProbeGrid` is retired (F3) and ours is a sparse brick volume, but the LIGHTING RIG the demo
 * ships is the point here, on the scene we use as our stress rig. Its ENTIRE lighting is:
 *
 *     sky = new SkyMesh();  sky.scale.setScalar( 450000 );          // in-scene, so the bake sees it
 *     dirLight = new THREE.DirectionalLight( 0xfff2dc, 100.0 );     // one shadow-caster
 *     probes = new LightProbeGrid( … );                             // the indirect term
 *     renderer.toneMapping = THREE.ACESFilmicToneMapping;
 *     renderer.toneMappingExposure = 1.0;
 *
 * `grep -i "ambient|hemisphere|environment|LightProbe("` over that file returns **nothing** beyond
 * the grid itself. It even strips the lights embedded in the glTF:
 *
 *     for ( const light of embeddedLights ) { if ( light.parent ) light.parent.remove( light ); }
 *
 * The probe grid is not a supplement to an ambient term in the reference. It **is** the ambient
 * term.
 *
 * ── The rule, and why it is a function rather than a comment ────────────────────────────────────
 *
 * A comment saying "turn the hemisphere down when you enable the grid" is the kind of thing that
 * was already written, at the site, and still did not happen for five days of measurement. So the
 * constant fill is no longer a number anybody sets: it is DERIVED from whether the volume is
 * carrying the indirect term, in one pure function, with the double-count made a reported fact
 * rather than an invisible one.
 *
 * The dev override survives — the four-state experiment (fill+grid, fill only, grid only, neither)
 * REQUIRES the double-counted state to exist so it can be photographed being wrong — but it can no
 * longer happen quietly: `doubleCounted` is in the snapshot, `scene.ts` warns once, and a test can
 * assert on it.
 *
 * NO SHIPPED DEFAULT CHANGES HERE. Every shipping world runs `gridOn === false`, and that arm
 * returns exactly today's 1.25.
 */

/**
 * The constant ambient fill used when NOTHING else carries the indirect term. This is the shipped
 * value and it is unchanged — `HemisphereLight(0xcfe0ec, 0x3a3a30, 1.25)` in `scene.ts`.
 */
export const AMBIENT_FILL_BASE = 1.25;

/**
 * What is left of the constant fill once an irradiance volume is carrying the indirect term.
 *
 * NOT zero, and the reason is a property of the representation rather than timidity: an L2 SH probe
 * field is baked from a finite cube resolution at a finite spacing, its cages go invalid inside
 * geometry, and it covers only the volume it was sized to. A surface the field cannot answer for
 * falls to whatever else is in the scene, and with a zero floor that is pure black — a hard,
 * unrecoverable clip rather than a soft one. The floor is deliberately SMALL relative to what the
 * volume delivers, so that it cannot flatten the spatial variation the volume exists to produce.
 *
 * ⚠️ **0.15 IS PROVISIONAL AND THE MEASUREMENT SAYS IT IS CURRENTLY TOO BIG.** Measured on
 * Sponza, 2026-08-31 (`tests/e2e/gi.e2e.ts` → G3, residual sweep at one pinned camera, one atlas,
 * one uniform moving): fill 0.1 → L 5.75 · 0.15 → 7.07 · 0.2 → 8.42 · 0.3 → 11.11, against the
 * volume ALONE at 3.35. So at 0.15 the "floor" is already contributing about as much light as the
 * volume it is supposed to be backstopping — which is the very failure this file exists to name.
 *
 * That ratio is not a property of the number; it is a property of the scene it was measured in.
 * G3.1 found our Sponza rig has essentially no direct light on its ground floor (the sun is 0.2 %
 * of the arcade frame), so the volume has almost nothing to carry and ANY constant dominates it.
 * The right residual is a small fraction of what the volume delivers, and it cannot be chosen
 * honestly until the direct lighting is fixed. Until then this is a conservative placeholder,
 * documented as one, and it changes no shipped default — no shipping world runs the volume.
 */
export const AMBIENT_FILL_RESIDUAL = 0.15;

/**
 * ── THE SKYLIT RESIDUAL (G4) — what is left of the constant fill when the volume is carrying a
 *    REAL SKY, and the number the G3 comment above said could not be chosen yet ─────────────────
 *
 * A skylight is a hemisphere light that knows about occlusion. `src/render/sky.ts` makes the sky a
 * real emitter in the scene so the probe bake photographs it, which is what turns the volume from
 * "a position-varying wash" into an actual skylight — and it is also what finally makes the residual
 * choosable, because the residual's whole job is to be SMALL RELATIVE TO WHAT THE VOLUME DELIVERS.
 *
 * The rule, fixed before the numbers so it survives a scene change: **the floor must stay a small
 * fraction — under a twentieth — of what the volume itself delivers, at EVERY measured viewpoint.**
 * Any larger and the constant starts competing with the variation it is backstopping, which is the
 * failure G3 photographed at 0.15; any smaller and a probe the field cannot answer for clips hard
 * to black with no recovery. Not zero, for that second reason: an L2 SH field baked at finite cube
 * resolution and finite spacing has real gaps, and the arcade's colonnade is full of them.
 *
 * Measured on Sponza with the sky baked in, sun 6, one session, one atlas, one uniform moving
 * (`tests/e2e/gi.e2e.ts` → G4.2's residual sweep). Percent added over the volume alone:
 *
 *     fill    arcade   banners   atrium
 *     0.02    +0.6 %    +1.1 %   +2.2 %
 *     0.05    +1.4 %    +2.8 %   +5.1 %   ← chosen: the largest under a twentieth everywhere
 *     0.10    +2.8 %    +5.7 %  +10.4 %
 *     0.15    +4.3 %    +8.6 %  +15.4 %   ← G3's provisional value; an eighth of the atrium frame
 *     1.25    +31.4 %  +67.7 % +105.1 %   ← the no-volume base, for scale
 *
 * The full derivation, and why it could not be done before the sky existed, is roadmap §G4.2.
 */
export const AMBIENT_FILL_SKYLIT_RESIDUAL = 0.05;

/** How far above the residual the fill may sit, with a volume present, before it is double-counting. */
const DOUBLE_COUNT_EPSILON = 1e-3;

/** The scene's indirect-lighting state — one owner, and a snapshot field so a test can read it. */
export interface IndirectTerm {
  /** The `HemisphereLight.intensity` to apply. Nothing else in the codebase may set that field. */
  readonly fill: number;
  /** True when an irradiance volume is present, visible and carrying the indirect term. */
  readonly gridOn: boolean;
  /**
   * True when that volume was baked with a REAL SKY emitter in the scene (`src/render/sky.ts`) —
   * i.e. the sky-through-volume IS the skylight, and the constant fill is down to a floor.
   */
  readonly skyBaked: boolean;
  /**
   * Where `fill` came from. `override` means a human asked for a specific number with `?hemi=`,
   * which is dev-only and is the experiment's lever.
   */
  readonly source: 'default' | 'grid-residual' | 'grid-residual-sky' | 'override';
  /**
   * **The fence.** True when a volume is carrying the indirect term AND the constant fill is still
   * above the residual floor — i.e. the frame is being given its indirect light twice. It can only
   * be true via an explicit dev override, it is in the snapshot, and `scene.ts` warns on it once.
   */
  readonly doubleCounted: boolean;
}

/**
 * Resolve the constant ambient fill from the state of the indirect term.
 *
 * @param gridOn   is an irradiance volume present, visible and carrying the indirect term?
 * @param override an explicit dev request (`?hemi=<n>` / the dev hook), or `null` for none.
 *                 Already range-checked by the caller; `null` and "0" are different answers and
 *                 the caller must not conflate them (`Number(null) === 0` — R1.6-F).
 */
export function resolveIndirect(
  gridOn: boolean,
  override: number | null,
  opts: {
    /**
     * Was a real sky emitter in the scene when this volume was baked? THE VOLUME'S property, not
     * the scene's: a sky added after the bake is not in the atlas, and a sky removed after the bake
     * still is. `scene.ts` therefore latches this at bake time, not at render time.
     */
    skyBaked?: boolean;
    /**
     * The world's base fill (`world.lighting.ambient.intensity`). Defaults to the shipped
     * `AMBIENT_FILL_BASE` so every existing caller and every world that says nothing is unchanged.
     */
    base?: number;
  } = {},
): IndirectTerm {
  const skyBaked = opts.skyBaked === true && gridOn;
  const base = opts.base ?? AMBIENT_FILL_BASE;
  const residual = skyBaked ? AMBIENT_FILL_SKYLIT_RESIDUAL : AMBIENT_FILL_RESIDUAL;
  const fill = override !== null ? override : gridOn ? residual : base;
  const source =
    override !== null
      ? 'override'
      : gridOn
        ? skyBaked
          ? 'grid-residual-sky'
          : 'grid-residual'
        : 'default';
  return {
    fill,
    gridOn,
    skyBaked,
    source,
    doubleCounted: gridOn && fill > residual + DOUBLE_COUNT_EPSILON,
  };
}

/* ───────────────────────────────────────────────────────────────────────────────────────────────
 * BAKE-ONLY LIGHTS — the same law, applied to punctual lights (G3.2)
 *
 * The owner's target is not "a nicer ambient". It is **UE's static-light model**: an artist places
 * lights that do not cast dynamic shadows, those lights are BAKED, and at runtime they cost
 * nothing. The probe volume is the vehicle — `probeGrid.bakeCapturePass` does
 * `cubeCamera.update(renderer, scene)` per capture, so ANY light in the scene at bake time is
 * integrated into the SH and is thereafter carried by the volume.
 *
 * Which makes the rule above apply again, unchanged and for the same reason:
 *
 *   **A light that is baked MUST NOT also be present at runtime.** Its energy is already in the
 *   atlas. Leaving it in the scene adds it a second time — the hemisphere-plus-volume error with a
 *   different light type — and it also throws away the entire point of baking, which is that the
 *   light stops costing anything per frame.
 *
 * So "bake-only" is a property OF THE LIGHT, marked before the bake and enforced by the bake
 * bracket rather than by discipline:
 *
 *   1. Mark: `userData[BAKE_ONLY] = true` (an artist's checkbox; today `?bakeLights=1`).
 *   2. `setProbeGrid` captures the marked lights, bakes with them present, and REMOVES them from
 *      the scene graph the moment `bake()` returns — inside the same try, so a throw cannot leave
 *      a baked light lit.
 *   3. A per-frame fence counts any frame that rendered a marked light while the atlas existed
 *      (`GiInfo.bakedLightLeaks`). Zero, forever. ⚠️ It was gated on the DENSE grid handle and was
 *      therefore inert on every sparse bake — found and retargeted at `sparseVolume` in F3, which
 *      is the reason it now has teeth rather than a history of reporting 0.
 *
 * REMOVAL, NOT `intensity = 0`, and the difference is the whole feature: a light set to zero is
 * still in the light set, still in the shader's loop, still costing its ~0.24 ms (unshadowed,
 * clustered) or ~3 ms (shadowed) per frame. Removing it is what makes a static light free. It also
 * changes three's light cache key, so the shader is rebuilt without it rather than keeping a dead
 * branch.
 */

/** `userData` key marking a light as bake-only: baked into the volume, removed before it renders. */
export const BAKE_ONLY = 'fwgBakeOnly';

/** True for an object that is a light marked bake-only. Narrow, so callers can traverse a scene. */
export function isBakeOnlyLight(o: {
  isLight?: boolean;
  userData?: Record<string, unknown>;
}): boolean {
  return o.isLight === true && o.userData?.[BAKE_ONLY] === true;
}

/* ───────────────────────────────────────────────────────────────────────────────────────────────
 * ⭐ THE HOLE FALLBACK — THE CONSTANT FILL, EXPRESSED AS SPHERICAL HARMONICS (S2, sparse spec §4.2c)
 *
 * ── The problem this exists to solve ───────────────────────────────────────────────────────────
 *
 * Surface-adaptive placement (S1) DELETES bricks that have no geometry near them. A deleted cell's
 * indirection texel points at `EMPTY_BRICK` — atlas slot (0,0,0) — whose 875 texels are zero (8,875
 * while F2’s octahedral depth bands were part of a probe). The
 * runtime then reads `s6.w = 0`, the sample-time validity divide floors at 1e-3, `.max(vec3(0))`
 * keeps it clean, and the fragment gets **exactly nothing**. Black. That is the same hard step
 * `fillEmptySpaces: false` produces by design, and adaptivity reintroduces it by a different door
 * on every world whose volumes reach past its geometry — which is all of them, because a volume is
 * an artist's box and geometry is not box-shaped.
 *
 * Measured on the owner's machine, on an atlas whose probes went dark for a DIFFERENT reason (a
 * `preview` bake with the validity capture off): **arcade 0.30 with the volume against 17.79
 * without — 98.3 % DARKER than no bake at all.** A hole is that failure, localised. Shipping
 * deletion without this would trade one black atlas for a holed one.
 *
 * ── Why the fix is a BAKE-TIME WRITE and not a runtime branch ──────────────────────────────────
 *
 * Slot 0 is already allocated, already never written by `scatterCaptures`, and already the slot the
 * indirection points at for a cell with no brick. So writing the world's constant hemisphere into
 * it costs:
 *
 *   · **zero shader instructions** — not one `select`, not one extra fetch. The seven SH samples
 *     and the validity divide happen either way; only the bytes they land on change. That is what
 *     keeps `tests/renderSparseAddress.test.ts`'s 9-load / 7-sample census unmoved, and the census
 *     is the fence that says the shader did not move.
 *   · **zero atlas bytes** — slot 0 is allocated today and holds zeros today.
 *   · **zero runtime cost** — same fetch, different contents.
 *
 * The three alternatives were priced and rejected at `probeCompile.ts`'s keep test: falling back to
 * a coarser level needs a second dependent indirection fetch on a term already at 1.14 ms/Mpix of a
 * 7.20 budget; pointing the indirection at a NEARBY brick makes `fract(coord · 2^-L)` land at an
 * arbitrary position inside a brick that does not contain the point (`probeCompile.ts` §2.6); and a
 * runtime cross-fade is v1's deleted `light.falloff` — a branch on a mutable, cache-key-invisible
 * field, R1.6-D's exact shape.
 *
 * ── ⚠️ AND `indirect.ts` STAYS THE LAW, WHICH IS WHY THIS ARITHMETIC LIVES HERE ─────────────────
 *
 * This file's whole content is *"a constant hemisphere and an irradiance volume are TWO
 * IMPLEMENTATIONS OF ONE TERM, and the frame may have one"*. A fill brick is not a third: it is
 * literally the constant hemisphere, delivered through the volume's own fetch, for the region the
 * volume has nothing to say about. Putting the coefficients in the GI atlas writer instead would be
 * a second place that decides what the ambient term is, which is the failure this file was written
 * to prevent. `probeScatter.ts` is handed the numbers and never learns where they came from — the
 * same seam `probeCompile` takes with the occupancy grid.
 *
 * ── THE DERIVATION, so nobody has to re-derive it from a screenshot ────────────────────────────
 *
 * `HemisphereLightNode.setup()` (three r185) is exactly:
 *
 *     irradiance = mix( groundColor·I, skyColor·I, dot(n, up)·0.5 + 0.5 )
 *                = I·[ 0.5(sky+ground) + 0.5(sky−ground)·(n·up) ]
 *
 * — a degree-1 polynomial in `n·up`, so it is EXACTLY representable in L2 SH with two nonzero
 * bands and no approximation error at all. `getShIrradianceAt` (three's `shGetIrradianceAt`, the
 * function `sparseFetch` calls) evaluates
 *
 *     result = c0·0.886227 + c1·(2·0.511664)·y + c2·(…)·z + c3·(…)·x + band-2 terms
 *
 * with `up = +Y` (three's `HemisphereLight` default, and the only pose any world here authors), so
 * matching the two forms coefficient by coefficient gives
 *
 *     c0 = I·0.5(sky+ground) / 0.886227     c1 = I·0.5(sky−ground) / 1.023328     c2…c8 = 0.
 *
 * ⚠️ The colours are LINEAR-space triples read off the live `HemisphereLight` object, never hex
 * strings off `world.lighting`. `THREE.Color` converts sRGB→linear on assignment and
 * `HemisphereLightNode` hands the shader the linear value, so a fill built from the hex would
 * describe a different light from the one that was photographed — the same discipline
 * `liveLighting()` keeps for every other provenance field.
 *
 * ⚠️ It is `up = +Y` ONLY. A world that poses its hemisphere light off-axis would get a fill whose
 * gradient points the wrong way — a wrong picture, not an error. No world authors one (the rig is
 * built once in `scene.ts` and never re-posed), and the day one does, this needs the rotation, not
 * a tweak. Named here rather than discovered there.
 */

/** `getShIrradianceAt`'s band-0 constant — `pi · 0.282095`, three's `shGetIrradianceAt`. */
const SH_IRRADIANCE_BAND0 = 0.886227;
/** …and its band-1 constant, `2 · 0.511664`. */
const SH_IRRADIANCE_BAND1 = 2.0 * 0.511664;

/** A linear-space RGB triple, read off a `THREE.Color`'s `r`/`g`/`b`. */
export type LinearRgb = readonly [number, number, number];

/**
 * The nine L2 SH coefficients (RGB each, in `getShIrradianceAt` order) whose irradiance is exactly
 * the constant hemisphere `mix(ground·I, sky·I, 0.5·n.y + 0.5)`.
 *
 * Exact, not fitted: the target is degree 1 in `n.y` and bands 0–1 span that space. The test
 * asserts the ROUND TRIP against the hemisphere formula at a spread of normals rather than against
 * these two numbers, so a change to three's constants fails here instead of shipping a wrong fill.
 */
export function hemisphereFillSh(
  skyColorLinear: LinearRgb,
  groundColorLinear: LinearRgb,
  intensity: number,
): Array<[number, number, number]> {
  const sh: Array<[number, number, number]> = [];
  for (let i = 0; i < 9; i++) sh.push([0, 0, 0]);
  for (let ch = 0; ch < 3; ch++) {
    const sky = skyColorLinear[ch]! * intensity;
    const ground = groundColorLinear[ch]! * intensity;
    sh[0]![ch] = (0.5 * (sky + ground)) / SH_IRRADIANCE_BAND0;
    sh[1]![ch] = (0.5 * (sky - ground)) / SH_IRRADIANCE_BAND1;
  }
  return sh;
}

/*
 * ⚠️ NO CPU MIRROR OF `getShIrradianceAt` LIVES HERE, DELIBERATELY. There is already exactly one
 * (`tests/shared/sh.ts` → `shIrradiance`, r185 coefficient for coefficient), and it is what the
 * fill brick's round trip is asserted against. A second copy in `src/` would be two definitions
 * of the decode that agree until someone edits one — the failure `sparseAddress.ts`'s header
 * spends a paragraph on. The producer lives here because it is a statement about the AMBIENT
 * TERM; the decode is a statement about three's basis, and it belongs with the other assertions.
 */

/**
 * ⭐ THE INTENSITY THE HOLE FILL IS WRITTEN AT, and it is NOT `AMBIENT_FILL_BASE`.
 *
 * A hole is shaded by two things at once: the fill brick it fetches, AND the `HemisphereLight` that
 * is still in the scene at the RESIDUAL (`resolveIndirect`'s volume arm — 0.05 skylit, 0.15 not).
 * Writing the full base into the brick would therefore light a hole at `base + residual`, i.e.
 * BRIGHTER than the same world with no GI at all — a hole that glows is as wrong as a hole that is
 * black, and it is worse in one way: it looks deliberate.
 *
 * So the brick carries the DIFFERENCE, and the sum at the fragment is exactly `base` — *"the
 * indirect term this world had before GI existed"*, which is the spec's own words for what a hole
 * should degrade to.
 *
 * Clamped at zero: `?hemi=` can put the live fill above the base, and a negative fill brick would
 * subtract light from a region that has none to give.
 */
export function holeFillIntensity(base: number, residual: number): number {
  return Math.max(0, base - residual);
}
